import { AuxPopoverTriggerEnum } from "@blk/aladdin-web-components";
import { AuxFacetedFilterOptionTypeEnum } from "components/ux/ins-faceted-filter/types";
import { language } from "language/default";
import { getDatePickerString } from "common/date-utils";
const formatPill = (key, values) => `${key} : ${values.length === 1 ? values[0].label : `${values.length} ${language.en.filter.selected}`}`;
const getCheckedFilterValues = (stateFilterValues, key) => {
    if (!stateFilterValues) {
        return [];
    }
    const values = stateFilterValues[key];
    return Array.isArray(values) ? values : [];
};
export const isElementChecked = (codes, stateFilterValues, key) => {
    const checkedFilters = getCheckedFilterValues(stateFilterValues, key);
    return codes.some((item) => checkedFilters?.includes(item));
};
export const isDateSelected = (isGlobal, filterData, stateFilterValues) => {
    if (isGlobal) {
        return filterData.startDateValue !== undefined || filterData.endDateValue !== undefined;
    }
    return stateFilterValues && stateFilterValues?.[filterData.field] !== undefined &&
        ((stateFilterValues?.[filterData.field]).startDate !== undefined ||
            (stateFilterValues?.[filterData.field]).endDate !== undefined);
};
export const isSliderSelected = (isGlobal, filterData, stateFilterValues) => {
    if (isGlobal) {
        return filterData.minValue !== undefined || filterData.maxValue !== undefined;
    }
    return stateFilterValues && stateFilterValues?.[filterData.field] !== undefined &&
        ((stateFilterValues?.[filterData.field]).lowerLimit !== undefined ||
            (stateFilterValues?.[filterData.field]).upperLimit !== undefined);
};
export const isPicklistSelected = (isGlobal, filterData, stateFilterValues) => {
    if (isGlobal) {
        return filterData.data.some((f) => f.isChecked || f.children.some((x) => x.isChecked));
    }
    return stateFilterValues && stateFilterValues?.[filterData.field] !== undefined &&
        (stateFilterValues?.[filterData.field]).length > 0;
};
const hasSelectedFilters = (filterData, stateFilterValues, isGlobal = false) => {
    if (filterData.type === "date-range") {
        return isDateSelected(isGlobal, filterData, stateFilterValues);
    }
    else if (filterData.type === "multi-slider") {
        return isSliderSelected(isGlobal, filterData, stateFilterValues);
    }
    else if (filterData.type === "picklist") {
        return isPicklistSelected(isGlobal, filterData, stateFilterValues);
    }
    return filterData.values.some((v) => (isGlobal ? v.checked : isElementChecked(v.codes, stateFilterValues, filterData.field)));
};
const isMultiSelectSelected = (entry, currentValues) => entry.type === AuxFacetedFilterOptionTypeEnum.MultiSelect &&
    Array.isArray(currentValues) &&
    entry.values.some((fil) => fil.codes.every((f) => currentValues.includes(f)));
const isDateRangeSelected = (entry, currentValues) => entry.type === AuxFacetedFilterOptionTypeEnum.DateRange &&
    !Array.isArray(currentValues) &&
    currentValues.type === "date" &&
    (currentValues.startDate || currentValues.endDate);
const isMultiSliderSelected = (entry, currentValues) => entry.type === AuxFacetedFilterOptionTypeEnum.MultiSlider &&
    !Array.isArray(currentValues) &&
    currentValues.type === "number-range" &&
    (currentValues.lowerLimit !== entry.minValue || currentValues.upperLimit !== entry.maxValue);
const isPickListSelected = (entry, currentValues) => entry.type === AuxFacetedFilterOptionTypeEnum.PickList &&
    Array.isArray(currentValues) &&
    currentValues.length > 0;
export const getNumberOfSelectedFilters = (filters, stateFilterValues) => {
    let counter = 0;
    filters.forEach((element) => {
        element.data.forEach((entry) => {
            const currentValues = stateFilterValues[entry.field];
            if (!currentValues) {
                return;
            }
            if (isMultiSelectSelected(entry, currentValues)) {
                counter += 1;
                return;
            }
            if (isDateRangeSelected(entry, currentValues)) {
                counter += 1;
                return;
            }
            if (isMultiSliderSelected(entry, currentValues)) {
                counter += 1;
                return;
            }
            if (isPickListSelected(entry, currentValues)) {
                counter += 1;
            }
        });
    });
    return counter;
};
const getGlobalSelectedFilters = (data) => {
    const selected = [];
    data.forEach((el) => {
        if (el.type === "multi-select" && el.values.some((x) => x.checked === true)) {
            selected.push(...data.filter((f) => f.type === "multi-select" && f.values.some((v) => v.checked && f.field === el.field)));
        }
        if (el.type === "date-range" && el.startDateValue !== undefined && el.endDateValue !== undefined) {
            selected.push(...data.filter((f) => f.type === "date-range" && f.field === el.field));
        }
        if (el.type === "multi-slider" && el.lowerLimit !== undefined && el.upperLimit !== undefined) {
            selected.push(...data.filter((f) => f.type === "multi-slider" && f.field === el.field));
        }
        if (el.type === "picklist" && el.data.some((f) => f.isChecked || f.children.some((x) => x.isChecked))) {
            selected.push(...data.filter((f) => f.type === "picklist" && f.field === el.field));
        }
    });
    return selected;
};
export const getValues = (e, stateFilterValues, isGlobal = false) => {
    if (e.type === "multi-select") {
        return e.values
            .filter((v) => (isGlobal ? v.checked : isElementChecked(v.codes, stateFilterValues, e.field)));
    }
    if (e.type === "picklist") {
        return e.data
            .filter((v) => v.isChecked || v.children.some((x) => x.isChecked));
    }
    if (e.type === "date-range" && e.startDateValue !== undefined && e.endDateValue !== undefined) {
        return [{ label: language.en.dashboard.filtersDate(getDatePickerString(e.startDateValue), getDatePickerString(e.endDateValue)) }];
    }
    if (e.type === "multi-slider") {
        return [{ label: `${e.lowerLimit} - ${e.upperLimit}` }];
    }
    return [];
};
const dedupeByField = (items) => {
    const seen = new Set();
    const result = [];
    items.forEach((item) => {
        if (!seen.has(item.field)) {
            seen.add(item.field);
            result.push(item);
        }
    });
    return result;
};
export const getFilters = (filters, stateFilterValues, isGlobal = false) => {
    const result = [];
    const filterData = [];
    filters.forEach((element) => {
        const { data } = element;
        if (isGlobal === false) {
            filterData.push(...data.filter((f) => hasSelectedFilters(f, stateFilterValues, false)));
        }
        else {
            filterData.push(...getGlobalSelectedFilters(data));
        }
    });
    const pills = dedupeByField(filterData).map((e) => ({
        auxId: e.field,
        displayValue: formatPill(e.label, getValues(e, stateFilterValues, isGlobal) ?? []),
        metadata: e.field,
        popoverProps: { isDataPoint: true, slotName: e.field, slotTag: e.field, trigger: AuxPopoverTriggerEnum.HOVER },
        type: "editable-popover"
    }));
    result.push(...pills);
    return result.length > 0 ? result : [];
};
export const getPopoverElement = (filters, stateFilterValues, isGlobal = false) => {
    const result = [];
    filters.forEach((element) => {
        const data = element.data
            .filter((f) => hasSelectedFilters(f, stateFilterValues, isGlobal));
        result.push(...data);
    });
    return dedupeByField(result);
};
